/**
 * Single source of truth for trial identity fields.
 *
 * Canonical storage: Project document (set via Projects sidebar → Create / Edit Trial).
 * - sponsorProtocolIdentifier → project.studyId
 * - trialAcronym → project.name
 * - sponsorName → project.sponsorName (legacy: project.description)
 *
 * Every screen that displays or exports these values should use the helpers below.
 * Sidebar / header chip: projectListPrimaryLabel → Sponsor Protocol Identifier only (no secondary line).
 * Editable from: the trial creation/edit dialog (Projects/Home) and the Study Registration
 * form (Sponsor Name, Trial Acronym) — both write through projectService.updateProject so the
 * Project document stays the single source of truth. Sponsor Protocol Identifier is immutable
 * once a trial exists and is not exposed as editable anywhere.
 */

export const PROJECT_IDENTITY_SOURCE_HINT =
  'Trial Acronym and Sponsor Name are optional when creating a trial (Projects sidebar → Create / Edit Trial). ' +
  'Sponsor Name becomes required in Study Registration.';

/**
 * Validate required trial identity on create/update. Only Sponsor Protocol Identifier is
 * required at this level — Trial Acronym and Sponsor Name are both optional here and are
 * filled in manually later (Sponsor Name is enforced as required specifically within the
 * Study Registration form, not at trial creation/edit).
 */
export const validateRequiredProjectIdentity = (fields = {}) => {
  const protocolId = String(
    fields.sponsorProtocolIdentifier ?? fields.studyId ?? ''
  ).trim();
  const errors = [];
  if (!protocolId) errors.push('Sponsor Protocol Identifier is required');
  return errors;
};

export const resolveProjectSponsorName = (project) => {
  if (!project) return '';
  return String(project.sponsorName || project.description || '').trim();
};

export const resolveProjectTrialAcronym = (project) => {
  if (!project) return '';
  return String(project.name || '').trim();
};

export const resolveProjectSponsorProtocolId = (project) => {
  if (!project) return '';
  return String(project.studyId || '').trim();
};

export const getProjectIdentityFields = (project) => ({
  trialAcronym: resolveProjectTrialAcronym(project),
  sponsorName: resolveProjectSponsorName(project),
  sponsorProtocolIdentifier: resolveProjectSponsorProtocolId(project),
});

export const projectListPrimaryLabel = (project) => {
  const protocolId = resolveProjectSponsorProtocolId(project);
  if (protocolId) return protocolId;
  const acronym = resolveProjectTrialAcronym(project);
  if (acronym) return acronym;
  return String(project?._id || '').trim() || 'Untitled Trial';
};

/** Sidebar secondary line: trial acronym when protocol ID is primary; otherwise sponsor name */
export const projectListSecondaryLabel = (project) => {
  const protocolId = resolveProjectSponsorProtocolId(project);
  const acronym = resolveProjectTrialAcronym(project);
  const sponsor = resolveProjectSponsorName(project);
  let secondary = sponsor || '';
  if (protocolId && acronym) secondary = acronym;
  else if (acronym && sponsor) secondary = sponsor;
  // Studies whose protocol ID and acronym are the same text would otherwise
  // render as "X — X" wherever primary and secondary are shown together.
  return secondary.trim() === projectListPrimaryLabel(project).trim() ? '' : secondary;
};

/** Technical key for KG/export when sponsor protocol identifier is absent */
export const resolveProjectTechnicalStudyKey = (project) => {
  if (!project) return '';
  return resolveProjectSponsorProtocolId(project) || String(project._id || '');
};

/**
 * Overlay canonical project identity onto any payload (admin, study reg, exports).
 * Project values win when present.
 */
export const withProjectIdentityFields = (project, fields = {}) => {
  if (!project) return { ...fields };
  const identity = getProjectIdentityFields(project);
  const next = { ...fields };
  const protocolId = identity.sponsorProtocolIdentifier;
  const trialAcronym = identity.trialAcronym || protocolId || String(next.trial_acronym || '').trim();

  if (trialAcronym) {
    next.trial_acronym = trialAcronym;
    next.trialAcronym = identity.trialAcronym || trialAcronym;
    if (identity.trialAcronym) {
      next.name = identity.trialAcronym;
    }
  }
  if (identity.sponsorName) {
    next.sponsor_name = identity.sponsorName;
    next.sponsorName = identity.sponsorName;
  }
  if (identity.sponsorProtocolIdentifier) {
    next.sponsor_protocol_identifier = identity.sponsorProtocolIdentifier;
    next.sponsor_primary_study_identifier = identity.sponsorProtocolIdentifier;
    next.sponsorProtocolIdentifier = identity.sponsorProtocolIdentifier;
    next.studyId = identity.sponsorProtocolIdentifier;
  }

  return next;
};

/** Prefer project identity, then saved administrative info. */
export const resolveTrialAcronymForDisplay = (project, adminInfo) => {
  return resolveProjectTrialAcronym(project) || String(adminInfo?.trial_acronym || '').trim();
};

export const resolveSponsorNameForDisplay = (project, adminInfo) => {
  return resolveProjectSponsorName(project) || String(adminInfo?.sponsor_name || '').trim();
};

export const resolveSponsorProtocolIdForDisplay = (project, adminInfo) => {
  return (
    resolveProjectSponsorProtocolId(project) ||
    String(adminInfo?.sponsor_protocol_identifier || '').trim()
  );
};

/** Human-readable message for DUPLICATE_PROJECT API errors (axios or plain). */
export const formatProjectDuplicateError = (error) => {
  const data = error?.response?.data;
  if (!data || data.code !== 'DUPLICATE_PROJECT') {
    return data?.message || error?.message || 'Failed to save trial';
  }

  let message = data.message || 'This trial already exists';
  const { duplicate, existing } = data;
  if (existing) {
    if (duplicate?.name && existing.name) {
      message += `\n\nExisting trial acronym: "${existing.name}"`;
      if (existing.studyId) message += `\nSponsor Protocol ID: ${existing.studyId}`;
    } else if (duplicate?.studyId && existing.studyId) {
      message += `\n\nExisting Sponsor Protocol ID: ${existing.studyId}`;
      if (existing.name) message += `\nTrial acronym: "${existing.name}"`;
    }
  } else if (duplicate?.name) {
    message += '\n\nChoose a different Trial Acronym, or leave it blank (it is optional).';
  } else if (duplicate?.studyId) {
    message += '\n\nChoose a different Sponsor Protocol Identifier.';
  }

  return message;
};
